/* Display only: never creates, estimates, or applies a Shopify discount. */
(() => {
  if (customElements.get('pb-cart-progress')) return;

  const thresholds = [2, 4, 6, 10];
  const discounts = [3, 5, 7, 9];
  const instances = new Set();
  let request;
  let revision = 0;

  function stateFor(count) {
    count = Math.max(0, Math.floor(Number(count) || 0));
    let tier = 0;
    thresholds.forEach((threshold, index) => {
      if (count >= threshold) tier = discounts[index];
    });
    const nextIndex = thresholds.findIndex((threshold) => count < threshold);
    let fill = 100;
    if (nextIndex === 0) fill = (count / 2) * 12.5;
    else if (nextIndex > 0) {
      const previous = thresholds[nextIndex - 1];
      fill = 12.5 + (nextIndex - 1) * 25 + ((count - previous) / (thresholds[nextIndex] - previous)) * 25;
    }
    return { count, tier, nextIndex, fill };
  }

  function renderAll(count) {
    instances.forEach((instance) => instance.render(count));
  }

  async function refresh(event = {}) {
    const currentRevision = ++revision;
    request?.abort();
    request = undefined;
    // change.js returns a complete cart. add.js generally returns added items only.
    if (Number.isInteger(event.cartData?.item_count)) {
      renderAll(event.cartData.item_count);
      return;
    }
    if (!instances.size) return;
    const controller = new AbortController();
    request = controller;
    const cartUrl = instances.values().next().value.dataset.cartUrl;
    try {
      const response = await fetch(cartUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!Number.isInteger(cart.item_count)) throw new Error('Invalid cart');
      if (currentRevision === revision) renderAll(cart.item_count);
    } catch (error) {
      if (error.name !== 'AbortError' && currentRevision === revision) {
        instances.forEach((instance) => instance.markUnavailable());
      }
    } finally {
      if (request === controller) request = undefined;
    }
  }

  class PureBauCartProgress extends HTMLElement {
    connectedCallback() {
      instances.add(this);
      this.render(this.dataset.count);
    }

    disconnectedCallback() {
      instances.delete(this);
    }

    render(count) {
      const state = stateFor(count);
      this.dataset.count = String(state.count);
      this.style.setProperty('--pb-progress-fill', `${state.fill}%`);
      this.querySelector('.pb-cart-progress__steps').hidden = false;
      const replace = (text, remaining, discount) => text
        .replaceAll('[remaining]', String(remaining))
        .replaceAll('[discount]', String(discount))
        .replaceAll('[count]', String(state.count));
      let message;
      if (state.nextIndex < 0) message = this.dataset.max;
      else {
        const remaining = thresholds[state.nextIndex] - state.count;
        message = state.tier
          ? `${replace(this.dataset.current, remaining, state.tier)} ${replace(remaining === 1 ? this.dataset.nextOne : this.dataset.next, remaining, discounts[state.nextIndex])}`
          : replace(remaining === 1 ? this.dataset.startOne : this.dataset.start, remaining, 4);
      }
      this.querySelector('[data-pb-message]').textContent = message;
      this.querySelectorAll('[data-pb-threshold]').forEach((step) => {
        step.classList.toggle('is-reached', state.count >= Number(step.dataset.pbThreshold));
      });
      const track = this.querySelector('[role="progressbar"]');
      track.setAttribute('aria-valuenow', String(Math.min(state.count, 10)));
      track.setAttribute('aria-valuetext', replace(this.dataset.accessible, 0, state.tier));
    }

    markUnavailable() {
      this.querySelector('[data-pb-message]').textContent = this.dataset.unavailable;
      this.querySelector('.pb-cart-progress__steps').hidden = true;
    }
  }

  customElements.define('pb-cart-progress', PureBauCartProgress);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
  }
  // Reconcile after back/forward navigation and when the cart drawer is opened.
  window.addEventListener('pageshow', (event) => { if (event.persisted) refresh(); });
  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('#cart-icon-bubble')) refresh();
  });
})();
